import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ConnectorAdminView,
  ConnectorSeedDocument,
  ConnectorUpsertResult,
  ConnectorVersionBody,
} from "@shared/types/connectorsAdmin";
import { ApiClientError, apiRequest } from "@/lib/api";

/**
 * Connector administration (FR-30 to FR-33).
 *
 * Two routes answer about one connector and both are used, because they hold
 * different halves of it: `/platform/connectors/:name` carries the run tunables,
 * the image tags and the seed checksum, while `/connectors/:name` carries the
 * seed's own field blocks, which the admin view deliberately leaves out because
 * they are large. The drawer reads both and says so.
 */
export const CONNECTORS_ADMIN_PATH = "/api/v2/platform/connectors";

const CONNECTORS_ADMIN_LIST_PATH = `${CONNECTORS_ADMIN_PATH}?includeInactive=true`;

export const CONNECTORS_ADMIN_QUERY_KEY = [CONNECTORS_ADMIN_LIST_PATH] as const;

/** The catalog read, which carries the seed's field blocks. Needs an instance header. */
export const CONNECTOR_CATALOG_PATH = "/api/v2/connectors";

/** The sentinel a disabled field-preview query parks on; see `use-instances-admin`. */
export const CONNECTOR_BLOCKS_DISABLED_KEY = "connector-blocks-disabled";

export function useConnectorsAdmin(enabled = true) {
  return useQuery<ConnectorAdminView[]>({ queryKey: CONNECTORS_ADMIN_QUERY_KEY, enabled });
}

/**
 * The seed's own blocks for one connector, for the read-only field preview.
 *
 * A failure here never breaks the drawer: the tunables and the tags come from
 * the admin view, so the preview degrades to "not available" rather than taking
 * the whole panel down. That is why it is a separate query rather than a second
 * field on the first one.
 */
export interface ConnectorSeedBlocks {
  name: string;
  displayName: string;
  fieldGroups: unknown;
  fieldDefinitions: unknown;
  credentialValidation: unknown;
  entitySelection: unknown;
  ui: unknown;
}

export function useConnectorSeedBlocks(name: string | null, enabled: boolean) {
  return useQuery<ConnectorSeedBlocks>({
    queryKey: [name ? `${CONNECTOR_CATALOG_PATH}/${name}` : CONNECTOR_BLOCKS_DISABLED_KEY],
    enabled: Boolean(name) && enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useAddConnectorVersion(name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectorVersionBody) =>
      apiRequest<ConnectorAdminView>(`${CONNECTORS_ADMIN_PATH}/${name}/versions`, {
        method: "POST",
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONNECTORS_ADMIN_QUERY_KEY }),
  });
}

export function useRemoveConnectorVersion(name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tag: string) =>
      apiRequest<ConnectorAdminView>(
        `${CONNECTORS_ADMIN_PATH}/${name}/versions/${encodeURIComponent(tag)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONNECTORS_ADMIN_QUERY_KEY }),
  });
}

/**
 * Make one tag the default (FR-32).
 *
 * There is no "set default" route: posting the tag again with `isDefault` is
 * the upsert the server already implements, and it demotes the previous default
 * in the same transaction. A duplicate-tag conflict is therefore expected on
 * this path and is NOT an error — the caller re-posts the tag it already has.
 */
export function useSetDefaultVersion(name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tag, label, notes }: { tag: string; label?: string; notes?: string }) =>
      apiRequest<ConnectorAdminView>(`${CONNECTORS_ADMIN_PATH}/${name}/versions`, {
        method: "POST",
        body: {
          tag,
          isDefault: true,
          ...(label ? { label } : {}),
          ...(notes ? { notes } : {}),
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONNECTORS_ADMIN_QUERY_KEY }),
  });
}

export function useSetConnectorActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, isActive }: { name: string; isActive: boolean }) =>
      apiRequest<ConnectorAdminView>(
        `${CONNECTORS_ADMIN_PATH}/${name}/${isActive ? "activate" : "deactivate"}`,
        { method: "POST" },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONNECTORS_ADMIN_QUERY_KEY }),
  });
}

/** The 200/201 body of the upsert route: the classification plus the row, when written. */
export type ConnectorUpsertResponse = ConnectorUpsertResult & {
  connector: ConnectorAdminView | null;
};

/**
 * Upsert a definition from its seed document (FR-31).
 *
 * `dryRun` classifies without writing, which is what the upload dialog does
 * first, and `force` applies a document the database has drifted from. The two
 * are the seed script's own `--dry-run` and `--force`, on purpose: one code
 * path means drift reported in CI and drift reported in this screen cannot
 * disagree.
 */
export function useUpsertConnectorSeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      seed,
      dryRun,
      force,
    }: {
      seed: ConnectorSeedDocument;
      dryRun: boolean;
      force: boolean;
    }) =>
      apiRequest<ConnectorUpsertResponse>(CONNECTORS_ADMIN_PATH, {
        method: "POST",
        body: { seed, dryRun, force },
      }),
    onSuccess: (result) => {
      if (result.applied) {
        void queryClient.invalidateQueries({ queryKey: CONNECTORS_ADMIN_QUERY_KEY });
      }
    },
  });
}

/** True when a failure is the server refusing a document, not a transport problem. */
export function isRefusal(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError && error.status >= 400 && error.status < 500;
}
